import { formatDate } from '../../../utils/dateFormat';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { investorService, KycDocumentRequirementDto } from '../../../services/investor.service';
import { pdfService } from '../../../services/pdf.service';
import { getActiveInvestorJourney } from '../../../services/investorJourneyScope';
import { PremiumJourneyStepper } from '../../../components/PremiumJourneyStepper/PremiumJourneyStepper';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';
import { BsEyeFill } from 'react-icons/bs';
import { SpFormsPanel } from '../../../components/SpFormsPanel/SpFormsPanel';
import { useJourneyStepAdvance } from '../../../hooks/useJourneyStepAdvance';
import '../InvestorProfile/InvestorProfile.scss';
import '../DocumentUpload/DocumentUpload.scss';

interface FileSelection {
  file: File;
  fileName: string;
  /** Dataverse ss_investordocumentsid — required for CRM-aligned upload */
  investorDocumentId?: string;
}

const EyeIcon = BsEyeFill as any;

export const OnboardingDocuments: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [requirements, setRequirements] = useState<KycDocumentRequirementDto | null>(null);
  const [dashboardData, setDashboardData] = useState<any>(null);
  const [uploadingFile, setUploadingFile] = useState<string | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<Map<string, FileSelection>>(new Map());
  const [showReasonsModal, setShowReasonsModal] = useState(false);
  const [kycComplete, setKycComplete] = useState(false);
  // All required KYC docs are uploaded but at least one still awaits the SP's approval —
  // the gate is the same, but the investor is told to WAIT, not to upload again.
  const [kycAwaitingApproval, setKycAwaitingApproval] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  // The CKYC/FATCA projection that used to live here is gone. Dataverse now returns real
  // ss_investordocuments rows for both — FATCA-INDIVIDUAL (ss_documenttype 100000013) and
  // CKYC-INDIVIDUAL (100000014) — each with its OWN ss_investordocumentsid, which is what the
  // projection was standing in for. Removing it also fixes what it warned about: it rebuilt the
  // list as exactly two rows cloned from a donor, so both forms shared one id (uploading one
  // marked both) and any other onboarding row Dataverse returned was silently dropped.

  /**
   * Deleting is how an investor re-opens a slot. The backend upsert would happily replace the
   * row, but the upload control is only offered when there is no active document (Laravel's
   * $showUpload rule), so an Approved / Submitted document is otherwise final.
   * Rejected and Sent Back slots re-open on their own and need no delete.
   */
  // Retained while the Delete button is commented out for Laravel parity (2026-09-10).
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  // Same as the KYC Docs page: ALWAYS stream via the authenticated endpoint. A stored
  // https:// SharePoint web URL must never be opened directly — investors have no SharePoint
  // account and land on AccessDenied. Onboarding uploads are kyc_documents rows, so the same
  // /me/documents/{id}/download serves them.
  const handlePreview = async (localDocumentId?: number) => {
    if (!localDocumentId) {
      toast.warning('Please upload a document first to preview');
      return;
    }
    try {
      const blob = await investorService.downloadDocument(localDocumentId);
      const url = URL.createObjectURL(blob);
      // No timed revoke: the preview tab's own Save/Download re-reads this URL, so revoking it
      // (previously after 60 s) made a late save fail with "Check internet connection". The
      // browser releases it when this page is closed or reloaded.
      window.open(url, '_blank');
    } catch (e) {
      toast.error('Unable to open document');
    }
  };

  const handleDeleteClick = async (localDocumentId?: number) => {
    if (!localDocumentId) {
      toast.warning('No document uploaded to delete');
      return;
    }
    if (!window.confirm('Are you sure you want to delete this document?')) return;
    try {
      await investorService.deleteDocument(localDocumentId);
      toast.success('Document deleted successfully');
      await loadData();
    } catch (err: any) {
      toast.error(err.response?.data?.error || err.response?.data?.message || 'Failed to delete document');
    }
  };

  // Silent status refresh for the step-advance hook: requirements + dashboard only, no
  // loading spinner, and it leaves the selected files alone (the poll is paused while the
  // batch upload is running).
  const refreshStatus = async () => {
    try {
      const [reqData, dashData] = await Promise.all([
        investorService.getOnboardingRequirements(),
        investorService.getDashboard(),
      ]);
      setRequirements(reqData);
      setDashboardData(dashData);
      return dashData;
    } catch {
      return null;
    }
  };

  // Once the SP has approved every onboarding document, tell the investor and continue to
  // the next step (Verification) — the same rule the stepper highlights with.
  useJourneyStepAdvance({ step: 'onboarding', dashboardData, refresh: refreshStatus, paused: uploadingFile !== null });

  const loadData = async () => {
    try {
      setLoading(true);
      const [onboardingReqs, dashboard] = await Promise.all([
        investorService.getOnboardingRequirements(),
        investorService.getDashboard()
      ]);

      console.log('📦 Onboarding Requirements:', onboardingReqs);
      console.log('📊 Dashboard Data:', dashboard);

      setRequirements(onboardingReqs);
      setDashboardData(dashboard);

      // Check if KYC is complete
      if (dashboard?.accountSummary) {
        // Laravel gates onboarding on APPROVED == REQUIRED, never on uploaded.
        const required = dashboard.accountSummary.kycDocumentsRequired;
        const uploaded = dashboard.accountSummary.kycDocumentsUploaded ?? 0;
        const approved =
          dashboard.accountSummary.kycDocumentsApproved ??
          dashboard.accountSummary.kycDocumentsUploaded;
        const isKycComplete = required > 0 && approved >= required;
        const isAwaitingApproval = !isKycComplete && required > 0 && uploaded >= required;
        console.log('✅ KYC Complete:', isKycComplete, '| awaiting approval:', isAwaitingApproval);
        setKycComplete(isKycComplete);
        setKycAwaitingApproval(isAwaitingApproval);

        if (!isKycComplete) {
          toast.warning(
            isAwaitingApproval
              ? "Your KYC documents are awaiting your service provider's approval."
              : 'Please complete KYC documents before accessing onboarding forms.'
          );
        }
      }
    } catch (error: any) {
      console.error('❌ Error loading onboarding data:', error);
      toast.error(error.response?.data?.message || 'Failed to load onboarding requirements');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (
    event: React.ChangeEvent<HTMLInputElement>,
    docDescription: string,
    investorDocumentId?: string
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file size (10 MB max)
    const maxSizeInBytes = 10 * 1024 * 1024;
    if (file.size > maxSizeInBytes) {
      toast.error('File size exceeds 10 MB. Please choose a smaller file.');
      event.target.value = '';
      return;
    }

    // Validate file type
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Only PDF, JPG, JPEG, PNG are allowed.');
      event.target.value = '';
      return;
    }

    // Store the selected file (keyed by description; include CRM row id for upload)
    setSelectedFiles(prev => {
      const updated = new Map(prev);
      updated.set(docDescription, { file, fileName: file.name, investorDocumentId });
      return updated;
    });
  };

  const handleBatchSubmit = async () => {
    if (selectedFiles.size === 0) {
      toast.error('Please select at least one file to upload');
      return;
    }

    if (!kycComplete) {
      toast.error(
        kycAwaitingApproval
          ? "Your KYC documents are awaiting your service provider's approval. Onboarding forms unlock once they are approved."
          : 'Please complete KYC documents first before uploading onboarding forms.'
      );
      return;
    }

    const failedUploads: string[] = [];
    const successfulUploads: string[] = [];

    setUploadingFile('batch');

    for (const [docDescription, fileSelection] of Array.from(selectedFiles.entries())) {
      if (!fileSelection.investorDocumentId?.trim()) {
        toast.error(
          `Missing CRM document ID for "${docDescription}". Refresh the page or contact support.`
        );
        failedUploads.push(docDescription);
        continue;
      }
      try {
        await investorService.uploadOnboardingDocument(
          fileSelection.file,
          docDescription,
          fileSelection.investorDocumentId
        );
        successfulUploads.push(docDescription);
      } catch (error: any) {
        console.error(`Error uploading ${docDescription}:`, error);
        failedUploads.push(docDescription);
      }
    }

    setUploadingFile(null);

    if (successfulUploads.length > 0) {
      toast.success(`Successfully uploaded ${successfulUploads.length} document(s)`);
      setSelectedFiles(new Map());
      await loadData();
    }

    if (failedUploads.length > 0) {
      toast.error(`Failed to upload: ${failedUploads.join(', ')}`);
    }
  };

  /**
   * Onboarding forms are gated on APPROVED KYC (Laravel: approved == required), which already
   * blocks uploads. Downloads were left open, so an investor whose KYC was still pending could
   * pull every form while the banner told them onboarding was locked (user 2026-09-10).
   * Returns false — and explains why — when the forms are not unlocked yet.
   */
  const ensureKycApproved = (): boolean => {
    if (kycComplete) return true;
    toast.error(
      kycAwaitingApproval
        ? "Your KYC documents are awaiting your service provider's approval. Onboarding forms unlock once they are approved."
        : 'Please complete KYC documents first before downloading onboarding forms.'
    );
    return false;
  };

  /** Locked styling for the three Download links, so they read as unavailable rather than broken. */
  const downloadLinkClass = kycComplete
    ? 'text-[#3e6f7c] ms-2 hover:underline font-semibold text-[10.5px]'
    : 'text-slate-400 ms-2 font-semibold text-[10.5px] cursor-not-allowed';
  const lockedDownloadTitle = kycAwaitingApproval
    ? 'Available once your service provider approves your KYC documents'
    : 'Complete your KYC documents to unlock this form';

  /** Same naming as the backend's PdfFilenames: {Form}_{First}_{Last}.pdf (used when the browser saves the blob). */
  const investorFilename = (formName: string): string => {
    const clean = (v: unknown) => String(v ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      .trim().replace(/[^A-Za-z0-9_-]+/g, '_').replace(/_+/g, '_').replace(/^_+|_+$/g, '').slice(0, 60);
    const person = [clean(dashboardData?.investor?.firstName), clean(dashboardData?.investor?.lastName)]
      .filter(Boolean).join('_');
    const base = clean(formName) || 'document';
    return `${person ? `${base}_${person}` : base}.pdf`;
  };

  /**
   * Saves a generated PDF as a named file. The old flow opened the blob in a new tab and revoked the
   * URL after 5 s — Chrome's own download button in that tab then failed with "Check internet
   * connection" and a UUID filename (UAT 2026-10-23). A named download needs no live tab; the URL is
   * kept for a minute so slow "Save as" dialogs still find it.
   */
  const saveBlobAsFile = (blob: Blob, filename: string) => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
  };

  /** "Download all" (client 2026-10-25): one zip with every document to print and sign for this journey. */
  const [downloadingAll, setDownloadingAll] = useState(false);
  const handleDownloadAll = async () => {
    if (!ensureKycApproved()) return;
    setDownloadingAll(true);
    try {
      toast.info('Preparing your onboarding documents, please wait...');
      const blob = await pdfService.downloadOnboardingPack(getActiveInvestorJourney() ?? undefined);
      if (blob.size === 0) throw new Error('The downloaded pack is empty');
      saveBlobAsFile(blob, investorFilename('Onboarding_Documents').replace(/\.pdf$/, '.zip'));
      toast.success('Onboarding documents downloaded');
    } catch (error: any) {
      console.error('[Onboarding pack] Error:', error);
      if (error?.response?.status === 404) {
        toast.warn('No onboarding documents are available to download yet.');
        return;
      }
      toast.error(error.message || 'Failed to download the onboarding documents. Please try again.');
    } finally {
      setDownloadingAll(false);
    }
  };

  const handleDownloadAccountOpeningBooklet = async (e: React.MouseEvent) => {
    e.preventDefault();
    if (!ensureKycApproved()) return;
    try {
      toast.info('Generating KYC Account Opening Kit PDF, please wait...');

      // The service provider's ACTIVE booklet template (admin SP Form Templates), pre-filled.
      const blob = await pdfService.downloadKycFormPdf(getActiveInvestorJourney() ?? undefined);

      console.log('[KYC Form PDF] Blob size:', blob.size, 'bytes');
      console.log('[KYC Form PDF] Blob type:', blob.type);

      if (blob.size === 0) {
        throw new Error('Generated PDF is empty');
      }

      saveBlobAsFile(blob, investorFilename('Account_Opening_Booklet'));
      toast.success('KYC Account Opening Kit PDF downloaded successfully');

    } catch (error: any) {
      console.error('[KYC Form PDF] Error:', error);
      if (error?.response?.status === 404) {
        toast.warn('Your service provider has not published the account opening booklet yet. Please check back later or contact them.');
        return;
      }
      toast.error(error.message || 'Failed to generate KYC Account Opening Kit PDF. Please try again.');
    }
  };

  const handleDownloadMasterDocument = async (e: React.MouseEvent, documentUrl: string | undefined, documentName: string) => {
    e.preventDefault();
    if (!ensureKycApproved()) return;

    if (!documentUrl) {
      toast.error('Document URL is not available');
      return;
    }

    try {
      toast.info('Downloading document, please wait...');

      const blob = await investorService.downloadSharePointDocument(documentUrl);

      if (blob.size === 0) {
        throw new Error('Downloaded file is empty');
      }

      // Create blob URL and trigger download
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = investorFilename(documentName);
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

      // Cleanup after a delay
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
      }, 1000);

      toast.success('Document downloaded successfully');

    } catch (error: any) {
      console.error('[Document Download] Error:', error);
      toast.error(error.response?.data?.message || error.message || 'Failed to download document. Please try again.');
    }
  };

  const getDocTypeValue = (doc: any): number | null => {
    const candidates = [doc?.documentType, doc?.documentTypeCode, doc?.ss_documenttype];
    for (const candidate of candidates) {
      if (candidate === undefined || candidate === null || candidate === '') {
        continue;
      }
      const parsed = Number(candidate);
      if (!Number.isNaN(parsed)) {
        return parsed;
      }
    }
    return null;
  };

  const isAccountOpeningBooklet = (doc: any): boolean => {
    if (getDocTypeValue(doc) === 100000012) {
      return true;
    }
    const description = String(doc?.description ?? '').trim().toLowerCase();
    return description === 'account opening booklet';
  };

  /**
   * Rows the platform GENERATES rather than fetching from SharePoint: the investor
   * downloads a pre-filled PDF, signs it, and uploads it back through the normal flow.
   *
   * Keyed on the Dataverse ss_documenttype option-set value, which is the stable identifier:
   *   100000012 = Account Opening Booklet
   *   100000013 = FATCA - Individual
   *   100000014 = CKYC - Individual
   *
   * Descriptions are kept only as a FALLBACK for rows written before the option-set values
   * existed. They are not a safe primary key — the live rows read "FATCA-INDIVIDUAL" and
   * "CKYC-INDIVIDUAL", so a keyword table has to guess at hyphenation and wording, and the
   * old 'investor info' keyword would also swallow the unrelated Investor Information
   * document (ss_documenttype 100000011) that Final Submit uses.
   */
  const GENERATED_FORM_TYPES: Record<number, 'ckyc' | 'fatca'> = {
    100000014: 'ckyc',
    100000013: 'fatca',
  };

  const GENERATED_FORM_KEYWORDS: Array<{ form: 'ckyc' | 'fatca'; keywords: string[] }> = [
    { form: 'ckyc', keywords: ['ckyc', 'central kyc'] },
    { form: 'fatca', keywords: ['fatca', 'crs'] },
  ];

  const generatedFormOf = (doc: any): 'ckyc' | 'fatca' | null => {
    if (isAccountOpeningBooklet(doc)) return null;
    // Dynamic Investor Uploads are SP-defined upload rows: never a generated form, whatever document
    // type the master was saved with (client 2026-10-25: "BROKERAGE PLAN V1" typed as FATCA offered the
    // pre-filled FATCA download and the signed FATCA ended up in the brokerage-plan row).
    if (doc?.dynamicUpload === true) return null;

    const typeValue = getDocTypeValue(doc);
    if (typeValue !== null && GENERATED_FORM_TYPES[typeValue]) {
      return GENERATED_FORM_TYPES[typeValue];
    }
    // Only fall back to text when the row carries no recognised type value at all, so a
    // typed row is never re-classified by a stray word in its description.
    if (typeValue !== null) return null;

    const description = String(doc?.description ?? '').trim().toLowerCase();
    if (!description) return null;
    const hit = GENERATED_FORM_KEYWORDS.find((rule) =>
      rule.keywords.some((kw) => description.includes(kw)));
    return hit ? hit.form : null;
  };

  const handleDownloadGeneratedForm = async (
    e: React.MouseEvent, form: 'ckyc' | 'fatca', label: string
  ) => {
    e.preventDefault();
    if (!ensureKycApproved()) return;
    // Audit 2026-08-25: this screen's requirements ARE journey-scoped (via the session
    // scope) — the generated forms must follow the SAME journey, not silently render the
    // primary journey's data. (The old comment claiming the page was unscoped was stale.)
    const jid = getActiveInvestorJourney() ?? undefined;
    const meta = form === 'ckyc'
      ? { file: investorFilename('CKYC_Application_Form'), fetch: () => pdfService.downloadCkycFormPdf(jid) }
      : { file: investorFilename('FATCA_CRS_Declaration'), fetch: () => pdfService.downloadFatcaFormPdf(jid) };

    try {
      toast.info(`Generating ${label}, please wait...`);
      const blob = await meta.fetch();
      if (blob.size === 0) {
        throw new Error('Generated PDF is empty');
      }
      saveBlobAsFile(blob, meta.file);
      toast.success(`${label} downloaded`);
    } catch (error: any) {
      console.error(`[${form} PDF] Error:`, error);
      toast.error(error?.message || `Failed to generate ${label}. Please try again.`);
    }
  };

  /** Documents the reviewer sent back — drives the Reasons drill-down, as on the KYC screen. */
  const rejectedDocs = (requirements?.documents ?? []).filter(
    doc => doc.localStatus?.toLowerCase() === 'rejected'
      || doc.localStatus?.toLowerCase() === 'sent back'
  );

  // Laravel: uploadedOnboardingCount >= totalOnboardingRequired && total > 0
  const allOnboardingUploaded =
    !!requirements &&
    requirements.totalRequired > 0 &&
    requirements.uploaded >= requirements.totalRequired;

  const getServiceProviderDisplayName = (): string => {
    const rawName = (requirements?.serviceProviderName || '').trim();
    if (!rawName || rawName.toLowerCase() === 'default tenant') {
      return 'Facilon Matrix Broker';
    }
    return rawName;
  };

  if (loading) {
    return (
      <div className="facilon-dashboard-wrapper">
        <main className="container-fluid dashboard-container-main">
          <div className="onboarding-documents">
            <div className="loading">Loading onboarding documents...</div>
          </div>
        </main>
      </div>
    );
  }

  console.log('🎨 Rendering OnboardingDocuments - requirements:', requirements, 'kycComplete:', kycComplete);

  return (
    <div className="facilon-dashboard-wrapper">
      <main className="container-fluid dashboard-container-main p-0">
        <div className="investor-profile px-3 px-md-0">
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden mb-2 shadow-sm">
            {/* Header Banner */}
            <div className="p-3 flex flex-col md:flex-row justify-between items-center text-white gap-3 md:gap-6" style={{ background: 'linear-gradient(90deg, rgba(5, 47, 85, 1), rgba(7, 87, 155, 1)), radial-gradient(circle at 82% 20%, rgba(28, 166, 217, 1), transparent 34%)' }}>
              <div className="flex-shrink-0">
                {/* Laravel: "Onboarding Documents Submission" / "As Required by {SP}" */}
                <h2 className="m-0 text-base font-bold text-white tracking-tight">Onboarding Documents Submission</h2>
                <p className="m-0 text-[11.5px] font-normal text-white/80 mt-0.5">
                  As Required by <strong>{getServiceProviderDisplayName()}</strong>
                </p>
              </div>
              <div className="flex-shrink-0">
                <PremiumJourneyStepper dashboardData={dashboardData} compact={true} />
              </div>
            </div>

            {/* Card Body */}
            <div className="p-3 bg-white">

            {/* Instruction Banner */}
            <div className="mb-3 p-2.5 bg-slate-50 rounded-lg border border-slate-200 text-[11px] text-slate-500 leading-relaxed">
              <div className="flex items-center gap-1.5 mb-1.5 text-[#3e6f7c] font-bold">
                <i className="bi bi-info-circle-fill" />
                <span>Onboarding Documents Process</span>
              </div>
              <ul className="list-disc ps-4 space-y-0.5">
                <li><strong>Download:</strong> Use the "Download" links to get the account opening booklet and any master templates.</li>
                <li><strong>Fill & Sign:</strong> Review carefully for accuracy, fill in any missing details, and sign.</li>
                <li><strong>Upload Soft Copy:</strong> Upload clear, self-attested soft copies through this platform.</li>
                <li><strong>Retain Physical Copies:</strong> Keep signed physical documents safe; some may need to be couriered or submitted in person later.</li>
              </ul>
            </div>

            {/* Document Upload Section */}
            {requirements && (
              <div className="flex flex-col gap-0">
                {!kycComplete && (
                  <div className="alert alert-warning mb-3 py-2 px-3 flex items-center justify-between flex-wrap gap-2 rounded-lg text-[11px] border border-amber-200 bg-amber-50 text-amber-800">
                    <span className="flex items-center gap-2">
                      {kycAwaitingApproval ? (
                        <>
                          <strong>⏳ Awaiting Service Provider Approval</strong>
                          <span>Your KYC documents are awaiting your service provider's approval. Onboarding forms unlock once they are approved.</span>
                        </>
                      ) : (
                        <>
                          <strong>⚠️ KYC Verification Required First!</strong>
                          <span>Please upload all KYC documents before uploading onboarding forms.</span>
                        </>
                      )}
                    </span>
                    <button className="inline-flex items-center px-2 py-0.5 text-[10px] font-semibold rounded-md bg-amber-100 text-amber-800 border border-amber-200 hover:bg-amber-200 transition-colors cursor-pointer" onClick={() => navigate('/investor/documents')}>
                      Go to KYC Documents
                    </button>
                  </div>
                )}

                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3 pb-2 pt-1">
                  {/* Laravel's onboarding instruction line, in its red. */}
                  <span className="text-[12px] font-medium" style={{ color: '#be1717' }}>
                    Please provide scanned &amp; signed copies of the requested documents (Allowed formats: PDF, JPG, JPEG | Max size: 10 MB per file).
                  </span>
                  <div className="flex flex-wrap items-center gap-1.5">
                    <span className="inline-flex items-center px-2 py-0.5 text-[10px] font-medium rounded-md bg-slate-100 text-slate-700 border border-slate-200">
                      Uploaded: <strong className="ms-1">{requirements.uploaded}/{requirements.totalRequired}</strong>
                    </span>
                    {/* Approved / Rejected counters and the Reasons drill-down, mirroring the KYC
                        screen. The backend already returns these on the onboarding requirements
                        payload; only the KYC page was rendering them. */}
                    <span className="inline-flex items-center px-2 py-0.5 text-[10px] font-medium rounded-md bg-[#ecfdf5] text-[#10b981] border border-[#10b981]/20">
                      Approved: <strong className="ms-1">{requirements.approved}</strong>
                    </span>
                    <span className="inline-flex items-center px-2 py-0.5 text-[10px] font-medium rounded-md bg-[#fef2f2] text-[#ef4444] border border-[#ef4444]/20">
                      Rejected: <strong className="ms-1">{requirements.rejected}</strong>
                    </span>
                    {rejectedDocs.length > 0 && (
                      <button
                        type="button"
                        className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold rounded-md bg-amber-50 text-amber-600 border border-amber-200 hover:bg-amber-100 transition-colors cursor-pointer"
                        onClick={() => setShowReasonsModal(true)}
                      >
                        <i className="bi bi-exclamation-triangle-fill" />
                        Reasons
                      </button>
                    )}
                  </div>
                </div>

                <div className="overflow-x-auto w-full border-t border-[#e2e8f0]">
                  <table className="w-full text-left border-collapse">
                    <thead className="bg-[#f1f5f9]">
                      <tr className="text-[10px] font-bold text-[#5a6e85] tracking-wider border-b border-[#e2e8f0]">
                        {/* Actions carries Delete only. Laravel has no actions column at all and
                            no delete — an uploaded document simply cannot be replaced there. We
                            keep delete because it is the only way to re-open a slot that is not
                            Rejected / Sent Back, matching the KYC screen. */}
                        <th className="pb-3.5 px-3 py-3" style={{ width: '45%' }}>Required Document</th>
                        <th className="pb-3.5 px-3 py-3" style={{ width: '15%' }}>Status</th>
                        <th className="pb-3.5 px-3 py-3" style={{ width: '30%' }}>Uploaded File / Upload Tool</th>
                        <th className="pb-3.5 px-3 py-3 text-right" style={{ width: '10%' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#e2e8f0]">
                      {[...requirements.documents]
                        .sort((a, b) => a.description.localeCompare(b.description))
                        .map((doc, index) => {
                          const selectedFile = selectedFiles.get(doc.description);
                          const isUploaded = doc.localRecordExists;
                          const isUploading = uploadingFile === doc.description;

                          // Key includes the description: while CKYC and FATCA are projected
                          // onto a single Dataverse row they share its id, and keying on that
                          // alone made React collapse them into one row.
                          return (
                            <tr key={`${doc.dynamicsId || 'row'}-${doc.description || index}`}
                                className="hover:bg-slate-50/50 transition-colors">
                              <td className="p-2 text-[12px] align-middle">
                                <div className="flex flex-col">
                                  <span className="font-semibold text-slate-700">
                                    {doc.description}
                                    {isAccountOpeningBooklet(doc) && (
                                      <a
                                        href="#"
                                        onClick={handleDownloadAccountOpeningBooklet}
                                        className={downloadLinkClass}
                                        aria-disabled={!kycComplete}
                                        title={kycComplete
                                          ? 'Download the filled account opening booklet'
                                          : lockedDownloadTitle}
                                      >
                                        <i className="bi bi-download me-1" />
                                        Download
                                      </a>
                                    )}
                                    {generatedFormOf(doc) && (
                                      <a
                                        href="#"
                                        onClick={(e) =>
                                          handleDownloadGeneratedForm(e, generatedFormOf(doc)!, doc.description)}
                                        className={downloadLinkClass}
                                        aria-disabled={!kycComplete}
                                        title={kycComplete
                                          ? `Download the pre-filled ${doc.description}`
                                          : lockedDownloadTitle}
                                      >
                                        <i className="bi bi-download me-1" />
                                        Download
                                      </a>
                                    )}
                                    {!isAccountOpeningBooklet(doc) && !generatedFormOf(doc) && doc.documentMasterUrl && (
                                      <a
                                        href="#"
                                        onClick={(e) => handleDownloadMasterDocument(e, doc.documentMasterUrl, doc.description)}
                                        className={downloadLinkClass}
                                        aria-disabled={!kycComplete}
                                        title={kycComplete ? 'Download the master template' : lockedDownloadTitle}
                                      >
                                        <i className="bi bi-download me-1" />
                                        Download
                                      </a>
                                    )}
                                  </span>
                                </div>
                              </td>
                              <td className="p-2 text-[12px] align-middle">
                                {/* Gate on the STATUS, not on localRecordExists. A Rejected /
                                    Sent Back document is soft-deleted so the slot reopens, which
                                    makes localRecordExists false — gating on it hid the rejection
                                    behind "NOT UPLOADED". Laravel keeps the two separate:
                                    $rawStatus drives the badge, $showUpload drives the control. */}
                                {doc.localStatus ? (
                                  <>
                                    {/* Laravel: the raw status word, green for
                                        submitted/approved and red otherwise. */}
                                    <span
                                      className="inline-flex items-center gap-1 text-[8.5px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wider"
                                      style={['submitted', 'approved'].includes((doc.localStatus || '').toLowerCase())
                                        ? { background: '#ecfdf5', color: '#10b981', border: '1px solid rgba(16,185,129,0.2)' }
                                        : { background: '#fef2f2', color: '#ef4444', border: '1px solid rgba(239,68,68,0.2)' }}
                                    >
                                      <i className={['submitted', 'approved'].includes((doc.localStatus || '').toLowerCase())
                                        ? 'bi bi-check-circle-fill'
                                        : 'bi bi-exclamation-circle-fill'} />
                                      {doc.localStatus || 'Uploaded'}
                                    </span>
                                    {['rejected', 'sent back'].includes((doc.localStatus || '').toLowerCase()) && (
                                      <div className="mt-1 text-[10px] font-semibold" style={{ color: '#dc3545' }}>
                                        Please check your mail.
                                      </div>
                                    )}
                                  </>
                                ) : (
                                  <label
                                    htmlFor={`doc_${index}`}
                                    className="inline-flex items-center gap-1 text-[8.5px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wider bg-[#f1f5f9] text-[#64748b] border border-slate-200 cursor-pointer hover:bg-slate-200 transition-colors"
                                    title="Click to choose and upload file"
                                  >
                                    <i className="bi bi-cloud-arrow-up-fill" />
                                    Not Uploaded
                                  </label>
                                )}
                              </td>
                              <td className="p-2 text-[12px] align-middle">
                                {isUploaded ? (
                                  <div className="flex items-center gap-2 text-slate-700">
                                    <i className="bi bi-file-earmark-check-fill text-[#10b981] text-lg flex-shrink-0" />
                                    <div>
                                      <div className="text-[11.5px] font-semibold text-slate-800 break-all" title={doc.fileName || 'Document'}>
                                        {doc.fileName || 'Onboarding Document'}
                                      </div>
                                      {doc.uploadedAt && (
                                        <div className="text-[10px] text-slate-400 mt-0.5">
                                          Uploaded {formatDate(doc.uploadedAt)}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                ) : (
                                  <div className="flex flex-col gap-1">
                                    <input
                                      type="file"
                                      id={`doc_${index}`}
                                      accept=".pdf,.jpg,.jpeg,.png"
                                      onChange={(e) => handleFileSelect(e, doc.description, doc.dynamicsId)}
                                      disabled={!kycComplete || isUploading}
                                      className="hidden"
                                    />
                                    <div className="flex items-center gap-2">
                                      <label
                                        htmlFor={`doc_${index}`}
                                        className={`inline-flex items-center text-[10px] font-bold h-[26px] px-2 rounded transition-all shadow-sm
                                          ${!kycComplete 
                                            ? 'bg-slate-100 text-slate-400 border border-slate-200 cursor-not-allowed'
                                            : selectedFile
                                              ? 'bg-[#ecfdf5] text-[#10b981] border border-[#10b981]/20 hover:bg-[#10b981]/10 cursor-pointer'
                                              : 'bg-[#eff6ff] text-[#3b82f6] border border-[#3b82f6]/20 hover:bg-[#3b82f6]/10 cursor-pointer'
                                          }
                                        `}
                                      >
                                        <i className="bi bi-file-earmark-arrow-up me-1.5" />
                                        {selectedFile ? selectedFile.fileName : 'Choose File'}
                                      </label>
                                      <span className="text-[10px] text-slate-400 font-normal">Max size: 10MB</span>
                                    </div>
                                  </div>
                                )}
                              </td>
                              {/* Preview (eye) was removed: it opened doc.documentUrl directly,
                                  which for SharePoint-backed rows is either a bare drive-item id
                                  (an invalid relative URL) or a folder URL the investor has no
                                  permission to open. Delete stays — deleting is what re-opens a
                                  slot whose document was not Rejected / Sent Back. */}
                              <td className="p-2 text-[12px] align-middle text-right">
                                {isUploaded && (
                                  <OverlayTrigger
                                    placement="top"
                                    overlay={<Tooltip id={`tooltip-preview-${doc.dynamicsId || index}`} className="text-[10px]">Preview Document</Tooltip>}
                                  >
                                    <button
                                      type="button"
                                      className="inline-flex items-center justify-center w-[28px] h-[28px] bg-[#e8edf2] hover:bg-[#dbe3eb] text-[#3e6f7c] rounded-full border-0 transition-all cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
                                      onClick={() => handlePreview(doc.localDocumentId)}
                                      disabled={!doc.localDocumentId}
                                    >
                                      <EyeIcon size={12} className="flex-shrink-0" />
                                    </button>
                                  </OverlayTrigger>
                                )}
                                {/* Delete hidden for Laravel parity (user 2026-09-10): Laravel's
                                    onboarding screen offers no delete. Commented rather than
                                    deleted because it is the ONLY way to re-open a slot whose
                                    document is not Rejected / Sent Back (see handleDeleteClick).
                                    Restore this block to undo.
                                <button
                                  type="button"
                                  className="inline-flex items-center justify-center w-[28px] h-[28px] bg-[#fff5f5] hover:bg-[#ffe4e6] text-[#e11d48] rounded-full border-0 transition-all cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
                                  onClick={() => handleDeleteClick(doc.localDocumentId)}
                                  disabled={!doc.localDocumentId || isUploading}
                                  title="Delete Document"
                                >
                                  <i className="bi bi-trash-fill" />
                                </button>
                                */}
                              </td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Laravel renders a single Upload control, disabled with an
                "All documents uploaded" note once nothing is outstanding. */}
            <div className="flex justify-center items-center gap-3 pt-3 border-t border-gray-100 mt-4">
              {/* "Download all" (client 2026-10-25): one zip with every document to print and sign; sits left of Upload (user 2026-10-26). */}
              <button
                type="button"
                onClick={handleDownloadAll}
                className="inline-flex items-center justify-center bg-white hover:bg-slate-50 text-[#3e6f7c] text-[11px] font-bold h-[32px] px-3.5 rounded-md border border-[#3e6f7c] transition-colors cursor-pointer shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={!kycComplete || downloadingAll}
                title={kycComplete ? 'Download every document to print and sign for this journey as one zip' : lockedDownloadTitle}
              >
                <i className={`bi ${downloadingAll ? 'bi-hourglass-split' : 'bi-file-earmark-zip'} me-1.5`} />
                {downloadingAll ? 'Preparing...' : 'Download all'}
              </button>
              <button
                type="button"
                onClick={handleBatchSubmit}
                className="inline-flex items-center justify-center bg-[#3e6f7c] hover:bg-[#355f69] text-white text-[11px] font-bold h-[32px] px-3.5 rounded-md border-0 transition-colors cursor-pointer shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={uploadingFile === 'batch' || selectedFiles.size === 0 || !kycComplete || allOnboardingUploaded}
              >
                <i className="bi bi-cloud-upload me-1.5" />
                {uploadingFile === 'batch' ? 'Uploading...' : 'Upload'}
              </button>
              {allOnboardingUploaded && (
                <small style={{ color: 'green' }}>All documents uploaded</small>
              )}
            </div>

            {/* Service-provider-specific forms (admin SP Form Templates); renders nothing when none are configured. */}
            <SpFormsPanel
              journeyId={getActiveInvestorJourney() ?? undefined}
              investorName={`${dashboardData?.investor?.firstName || ''} ${dashboardData?.investor?.lastName || ''}`.trim()}
              disabled={!kycComplete}
              disabledReason="Complete your KYC first — the forms are filled from your KYC details."
            />
          </div>
          </div>
        </div>
      </main>

      {/* Rejection reasons — same drill-down as the KYC screen. */}
      {showReasonsModal && (
        <div className="fixed inset-0 z-[2000] flex items-center justify-center bg-black/40" onClick={() => setShowReasonsModal(false)}>
          <div className="bg-white rounded-xl border border-gray-200 max-w-[500px] w-[90%] max-h-[80vh] overflow-y-auto shadow-md animate-fadeIn" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center p-3 border-b border-gray-200">
              <h2 className="m-0 text-[14px] font-bold text-slate-800">Rejection Reasons</h2>
              <button className="text-[20px] text-slate-400 hover:text-slate-600 bg-transparent border-0 cursor-pointer p-0 leading-none" onClick={() => setShowReasonsModal(false)}>×</button>
            </div>
            <div className="p-3">
              {rejectedDocs.length === 0 ? (
                <p className="text-[12px] text-slate-500 m-0">No rejected documents.</p>
              ) : (
                <div className="flex flex-col gap-2.5">
                  {rejectedDocs.map((doc) => (
                    <div key={`${doc.dynamicsId}-${doc.description}`} className="border border-red-200 bg-red-50/50 p-2.5 rounded-lg text-[#991b1b]">
                      <h4 className="m-0 text-[12px] font-bold text-red-700">{doc.description}</h4>
                      <p className="m-0 mt-1 text-[11px]"><strong>Status:</strong> {doc.localStatus}</p>
                      <p className="m-0 mt-0.5 text-[11px]"><strong>Reason:</strong> {doc.reason || 'No reason provided'}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="p-3 border-t border-gray-200 flex justify-end">
              <button
                type="button"
                className="inline-flex items-center justify-center bg-[#3e6f7c] hover:bg-[#355f69] text-white text-[11px] font-bold h-[30px] px-3 rounded-md border-0 transition-colors cursor-pointer"
                onClick={() => setShowReasonsModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
